/**
 * P2PAssistPanel - P2P 辅助打洞调试面板（方案 B 仪表盘 + 保留折叠区）
 *
 * 布局:
 *   [标题栏]
 *   [4 张指标卡: 主连接 / RTT / 尝试进度 / 打洞状态]
 *   [同步信息区]
 *   [当前阶段进度条]
 *   [最佳通道（含手动切换按钮）]
 *   [折叠区: 已建立P2P / 当前尝试详情]
 *   日志/历史不再展示在面板上，通过顶部「打洞日志」「打洞报告」按钮复制查看
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { eventBus } from '../core/EventBus';
import { p2pAssistService, StepSides } from '../core/P2PAssistService';
import type { LogEntry, StepStatus, SyncInfo, FilteredAttempt } from '../core/P2PAssistService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface Position { x: number; y: number; }

// 当前尝试的 4 阶段划分（时间顺序：信令协商 → Sync 协同 → ICE 打洞 → 质量验证）
// stepIds 来自 P2PAssistService 的 Steps 常量
type PhaseKey = 'signal' | 'sync' | 'ice' | 'quality';
interface PhaseDef {
  key: PhaseKey;
  title: string;
  stepIds: number[];
  color: string;
}
const PHASES: PhaseDef[] = [
  { key: 'signal',  title: '① 信令协商', stepIds: [1, 2, 3, 4, 5, 6, 7, 8, 9],   color: '#64b5f6' },
  { key: 'sync',    title: '② Sync 协同', stepIds: [17, 18, 19],                   color: '#ab47bc' },
  { key: 'ice',     title: '③ ICE 打洞',  stepIds: [10, 11],                       color: '#ffb74d' },
  { key: 'quality', title: '④ 质量验证',  stepIds: [12, 13, 14, 15, 16],           color: '#81c784' },
];

interface PhaseAgg {
  status: 'pending' | 'running' | 'success' | 'failed' | 'skipped';
  durationMs?: number;
  failedStep?: StepStatus;
  successCount: number;
  totalCount: number;
  hasData: boolean;      // 是否有任何 step 已经开始（非全 pending）
}

// 聚合一个阶段的状态 + 耗时
function aggregatePhase(steps: StepStatus[], stepIds: number[], nowMs: number): PhaseAgg {
  const subs = steps.filter(s => stepIds.includes(s.step));
  const total = subs.length;
  const running = subs.find(s => s.status === 'running');
  const failed = subs.find(s => s.status === 'failed');
  const successCount = subs.filter(s => s.status === 'success').length;
  const skippedCount = subs.filter(s => s.status === 'skipped').length;
  const pendingCount = subs.filter(s => s.status === 'pending').length;

  let status: PhaseAgg['status'];
  if (failed) status = 'failed';
  else if (running) status = 'running';
  else if (total === 0) status = 'pending';
  else if (pendingCount === total) status = 'pending';
  else if (skippedCount === total) status = 'skipped';
  else if (successCount + skippedCount === total && successCount > 0) status = 'success';
  else status = 'running'; // 部分成功、其余 pending —— 推进中

  // 耗时：最早 startTime → 最晚 endTime（或当前时间）
  const starts = subs.map(s => s.startTime).filter((v): v is number => typeof v === 'number');
  const ends = subs.map(s => s.endTime).filter((v): v is number => typeof v === 'number');
  let durationMs: number | undefined;
  if (starts.length > 0) {
    const s0 = Math.min(...starts);
    if ((status === 'success' || status === 'failed') && ends.length > 0) {
      durationMs = Math.max(...ends) - s0;
    } else if (status === 'running') {
      durationMs = nowMs - s0;
    }
  }

  return {
    status,
    durationMs,
    failedStep: failed,
    successCount,
    totalCount: total,
    hasData: starts.length > 0,
  };
}

const P2PAssistPanel: React.FC<Props> = ({ isOpen, onClose }) => {
  const [state, setState] = useState(p2pAssistService.getState());
  // tick 一次推动 UI 更新：强制切换倒计时 + 当前尝试阶段已耗时
  const [nowMs, setNowMs] = useState<number>(Date.now());
  useEffect(() => {
    // 条件：正在打洞（刷新阶段计时），或已有 first established（刷新 60s 倒计时）
    if (!state.isRunning && !state.firstEstablishedAt) return;
    const t = window.setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(t);
  }, [state.firstEstablishedAt, state.isRunning]);

  // 拖动（沿用原逻辑，简化）
  const [position, setPosition] = useState<Position>({ x: window.innerWidth - 620, y: 60 });
  const panelRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const positionRef = useRef(position);
  const startRef = useRef({ x: 0, y: 0, left: 0, top: 0 });

  // 折叠（默认打开最佳通道、已建立；"被过滤的 P2P"默认折叠；"当前尝试详情"强制常开不在此）
  const [sections, setSections] = useState({
    established: true,
    filtered: false,
  });

  // 当前尝试详情：调试模式（展开原 20 步 Timeline），仅本会话有效
  const [debugMode, setDebugMode] = useState<boolean>(false);
  const toggleDebugMode = () => {
    setDebugMode(v => !v);
  };

  useEffect(() => {
    const h = (s: typeof state) => setState(s);
    eventBus.on('p2p_assist:stateChange', h);
    return () => { eventBus.off('p2p_assist:stateChange', h); };
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    startRef.current = { x: e.clientX, y: e.clientY, left: positionRef.current.x, top: positionRef.current.y };
    isDraggingRef.current = true;
  }, []);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const pad = 10;
      const np = {
        x: Math.max(pad, Math.min(window.innerWidth - 50, startRef.current.left + e.clientX - startRef.current.x)),
        y: Math.max(pad, Math.min(window.innerHeight - 50, startRef.current.top + e.clientY - startRef.current.y)),
      };
      positionRef.current = np;
      setPosition(np);
    };
    const onUp = () => { isDraggingRef.current = false; };
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('mouseup', onUp, true);
    return () => {
      document.removeEventListener('mousemove', onMove, true);
      document.removeEventListener('mouseup', onUp, true);
    };
  }, []);

  if (!isOpen) return null;

  // 当前尝试：优先跟随正在跑的 attempt；越界场景按 isRunning 分流：
  // - 运行中（一轮 6 个跑完等下一轮间隔）→ 显示这一轮最后一个 attempt
  //   （避免显示 bestAttempt 造成 "本轮进度 6/6 vs 当前尝试 #2 完成" 的认知错位）
  // - 非运行（切换后/停止后）→ 返回 null，让"当前尝试详情"区域回到"等待开始..."
  //   （bestAttempt 仍保留在 state 里供"打洞报告"按钮读取，不影响报告功能）
  let currentAttempt = state.attempts[state.currentAttemptIndex];
  if (!currentAttempt) {
    currentAttempt = state.isRunning ? state.attempts[state.attempts.length - 1] : null;
  }
  const syncInfo: SyncInfo | null = state.syncInfo || null;

  const toggle = (k: keyof typeof sections) =>
    setSections(p => ({ ...p, [k]: !p[k] }));

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('zh-CN', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
      + '.' + String(d.getMilliseconds()).padStart(3, '0');
  };

  // 打洞状态：默认认为都可打洞，不再做 NAT 分型预测
  //   - established: 已有 P2P 连接事实存在 / 主连接已是 P2P
  //   - running:     正在打洞中（有 running 的 attempt）
  //   - idle:        未开始
  type HolePunchStatus = 'established' | 'running' | 'idle';
  const holePunchStatus: HolePunchStatus = (() => {
    if (state.establishedConnections.length > 0) return 'established';
    if (state.mainConnectionType === 'p2p') return 'established';
    if (state.isRunning) return 'running';
    return 'idle';
  })();

  // 阶段进度（4 个段，NAT 探测已移除）
  const phases: { key: SyncInfo['phase']; label: string; icon: string }[] = [
    { key: 'rtt', label: 'RTT', icon: '⏱' },
    { key: 'sync', label: 'Sync', icon: '⚡' },
    { key: 'fired', label: '点火', icon: '🔥' },
    { key: 'ice', label: 'ICE', icon: '🔗' },
  ];
  const phaseOrder: Record<string, number> = { rtt: 0, sync: 1, fired: 2, ice: 3, quality: 4, done: 5 };
  const curPhase = syncInfo?.phase || 'rtt';
  const curPhaseIdx = phaseOrder[curPhase] ?? 0;
  const phaseProgress = Math.min(100, Math.round((curPhaseIdx / (phases.length)) * 100));

  // ---------- 渲染组件 ----------
  const Card: React.FC<{ title: string; value: React.ReactNode; sub?: string; color?: string }> = ({ title, value, sub, color }) => (
    <div style={{
      flex: 1,
      background: '#252525',
      border: '1px solid #333',
      borderRadius: 6,
      padding: '10px 8px',
      textAlign: 'center',
      minWidth: 0,
    }}>
      <div style={{ fontSize: 18, fontWeight: 'bold', color: color || '#fff', lineHeight: 1.2 }}>{value}</div>
      <div style={{ fontSize: 9, color: '#888', marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {sub && <div style={{ fontSize: 9, color: '#666', marginTop: 2 }}>{sub}</div>}
    </div>
  );

  const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
    <span style={{
      display: 'inline-block',
      padding: '1px 6px',
      borderRadius: 3,
      background: `${color}22`,
      color,
      border: `1px solid ${color}66`,
      fontSize: 10,
      marginLeft: 4,
    }}>{children}</span>
  );

  const SectionHeader: React.FC<{ title: string; sec: keyof typeof sections; count?: number; color?: string }> = ({ title, sec, count, color }) => (
    <div
      onClick={() => toggle(sec)}
      style={{
        padding: '6px 8px', background: '#2a2a2a', fontWeight: 'bold', fontSize: 12,
        cursor: 'pointer', display: 'flex', alignItems: 'center', userSelect: 'none',
        borderBottom: sections[sec] ? '1px solid #333' : 'none',
      }}
    >
      <span style={{
        marginRight: 6, fontSize: 10,
        transform: sections[sec] ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.2s', display: 'inline-block',
      }}>▶</span>
      <span style={{ color: color || '#e0e0e0' }}>{title}</span>
      {count !== undefined && <span style={{ marginLeft: 8, color: '#888', fontWeight: 'normal' }}>({count})</span>}
    </div>
  );

  const getStepColor = (s: StepStatus['status']) =>
    ({ success: '#4caf50', failed: '#f44336', running: '#2196f3', skipped: '#666', pending: '#888' }[s] || '#888');
  const getStepIcon = (s: StepStatus['status']) =>
    ({ success: '✓', failed: '✗', running: '◌', skipped: '-', pending: '○' }[s] || '○');
  const getLogStyle = (l: LogEntry['level']) =>
    ({ color: { error: '#f44336', warning: '#ff9800', success: '#4caf50', info: '#e0e0e0' }[l] || '#e0e0e0' });

  // 打洞方式徽章
  const holePunchBadge = (m?: SyncInfo['holePunchMethod']) => {
    if (m === 'sync') return <Badge color="#ab47bc">同步打洞</Badge>;
    if (m === 'fallback_immediate') return <Badge color="#ff9800">降级</Badge>;
    if (m === 'legacy') return <Badge color="#64b5f6">常规</Badge>;
    return null;
  };

  // ---------- 渲染 ----------

  // 最佳通道（质量最好的已完成 P2P 通道）
  const renderBestChannel = () => {
    if (!state.bestAttempt) {
      return (
        <div style={{ padding: 10, color: '#888', fontSize: 11, textAlign: 'center' }}>
          暂无已完成的打洞通道
        </div>
      );
    }
    const best = state.bestAttempt;
    const r = best.result;
    // 根据 bestAttempt 对应 session 找 syncInfo（若已失去 session 则用顶层）
    const bestSync = syncInfo;
    return (
      <div style={{ padding: '8px 10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ color: '#ffc107', fontSize: 14 }}>⭐</span>
          <span style={{ flex: 1, fontSize: 12, fontWeight: 'bold' }}>
            {best.stunServers.map(s => s.replace('stun:', '')).join(' + ')}
          </span>
          <span style={{ fontSize: 11, color: '#4caf50' }}>{r?.throughputKbps ?? '-'} kbps</span>
          <span style={{ fontSize: 10, color: '#64b5f6' }}>{r?.connectionType ?? '-'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: '#aaa', marginBottom: 6 }}>
          <span>打洞方式:</span>
          {holePunchBadge(bestSync?.holePunchMethod) || <span style={{ color: '#666' }}>未知</span>}
          {bestSync?.syncAccuracyMs !== undefined && (
            <>
              <span style={{ marginLeft: 6 }}>Sync精度:</span>
              <span style={{ color: '#ffc107' }}>±{bestSync.syncAccuracyMs}ms</span>
            </>
          )}
          {r?.rttMs !== undefined && (
            <>
              <span style={{ marginLeft: 6 }}>RTT:</span>
              <span style={{ color: '#81c784' }}>{r.rttMs.toFixed(0)}ms</span>
            </>
          )}
        </div>
        {(() => {
          const isPending = state.pendingPrepareSessionId === state.bestAttempt?.sessionId;
          const label = isPending ? '准备中…' : '点击切换';
          return (
            <button
              onClick={() => p2pAssistService.performSwitch()}
              disabled={isPending}
              style={{
                width: '100%', padding: '6px 10px',
                background: isPending ? '#555' : '#4caf50',
                border: 'none', borderRadius: 4, color: 'white',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontSize: 11, fontWeight: 'bold',
                opacity: isPending ? 0.7 : 1,
              }}
            >
              {label}
            </button>
          );
        })()}
      </div>
    );
  };

  // 已建立 P2P（每条带"切换"按钮，主连接是 P2P 时不显示按钮）
  const isAlreadyP2P = state.mainConnectionType === 'p2p';
  const renderEstablished = () => (
    <div style={{ maxHeight: 220, overflowY: 'auto' }}>
      {state.establishedConnections.length === 0 ? (
        <div style={{ padding: 12, textAlign: 'center', color: '#888', fontSize: 12 }}>暂无</div>
      ) : state.establishedConnections.map((conn) => (
        <div key={conn.sessionId} style={{
          padding: 8, borderBottom: '1px solid #333',
          background: state.bestAttempt?.sessionId === conn.sessionId ? 'rgba(76,175,80,0.1)' : 'transparent',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 11, color: (conn.iceState === 'connected' || conn.iceState === 'completed') ? '#4caf50' : '#ff9800' }}>●</span>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 'bold' }}>
              {conn.stunServers.map(s => s.replace('stun:', '')).join(', ')}
            </span>
            {state.bestAttempt?.sessionId === conn.sessionId && (
              <span style={{ fontSize: 10, color: '#ffc107', fontWeight: 'bold' }}>最佳</span>
            )}
            {!isAlreadyP2P && (() => {
              const isPending = state.pendingPrepareSessionId === conn.sessionId;
              return (
                <button
                  onClick={() => p2pAssistService.performSwitchTo(conn.sessionId)}
                  disabled={isPending}
                  style={{
                    padding: '2px 8px', fontSize: 10,
                    background: isPending ? '#555' : '#4caf50',
                    color: 'white', border: 'none', borderRadius: 3,
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    opacity: isPending ? 0.7 : 1,
                  }}
                >
                  {isPending ? '准备中…' : '点击切换'}
                </button>
              );
            })()}
          </div>
          {/* N2/N3: grid 加 RTT + ICE state 改 Badge */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 10, marginLeft: 16 }}>
            <div><span style={{ color: '#888' }}>吞吐: </span><span style={{ color: '#4caf50' }}>{conn.throughputKbps} kbps</span></div>
            <div>
              <span style={{ color: '#888' }}>ICE: </span>
              {(() => {
                const s = conn.iceState || '-';
                let bg = '#888';
                if (s === 'connected' || s === 'completed') bg = '#4caf50';
                else if (s === 'failed') bg = '#f44336';
                else if (s === 'disconnected') bg = '#ff9800';
                return (
                  <span style={{
                    display: 'inline-block', padding: '0 5px', fontSize: 9,
                    background: `${bg}22`, color: bg, border: `1px solid ${bg}66`, borderRadius: 3,
                  }}>{s}</span>
                );
              })()}
            </div>
            <div><span style={{ color: '#888' }}>类型: </span><span style={{ color: '#64b5f6' }}>{conn.connectionType}</span></div>
            <div><span style={{ color: '#888' }}>RTT: </span><span style={{ color: '#81c784' }}>{conn.rttMs !== undefined ? `${Math.round(conn.rttMs)}ms` : '-'}</span></div>
            <div><span style={{ color: '#888' }}>轨道: </span>{conn.hasVideo ? 'V' : '-'}/{conn.hasAudio ? 'A' : '-'}</div>
          </div>
          {(conn.localCandidate || conn.remoteCandidate) && (
            <div style={{ fontSize: 10, color: '#aaa', marginLeft: 16, marginTop: 4, lineHeight: 1.5 }}>
              {conn.localCandidate && (
                <div><span style={{ color: '#64b5f6' }}>客户端:</span> <span style={{ fontFamily: 'monospace' }}>{conn.localCandidate}</span></div>
              )}
              {conn.remoteCandidate && (
                <div><span style={{ color: '#81c784' }}>服务端:</span> <span style={{ fontFamily: 'monospace' }}>{conn.remoteCandidate}</span></div>
              )}
            </div>
          )}
          <div style={{ fontSize: 10, color: '#666', marginLeft: 16, marginTop: 4 }}>建立于 {formatTime(conn.establishedAt)}</div>
        </div>
      ))}
    </div>
  );

  // 被过滤的 P2P 列表（质量不达标 / 各阶段失败 / 建立后断开）
  const filterCategoryStyle = (c: FilteredAttempt['filterCategory']) => {
    switch (c) {
      case 'dropped': return { color: '#ff9800', label: '建立后断开' };
      case 'quality': return { color: '#f44336', label: '质量不达标' };
      case 'signal':  return { color: '#9e9e9e', label: '信令错误' };
      case 'ice':     return { color: '#9e9e9e', label: 'ICE 失败' };
      case 'sync':    return { color: '#9e9e9e', label: 'Sync 失败' };
      default:        return { color: '#666',    label: '其他' };
    }
  };
  const renderFiltered = () => {
    const list = state.filteredAttempts || [];
    if (list.length === 0) {
      return <div style={{ padding: 12, textAlign: 'center', color: '#888', fontSize: 12 }}>暂无被过滤的连接</div>;
    }
    return (
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {list.map((f) => {
          const a = f.attempt;
          const cat = filterCategoryStyle(f.filterCategory);
          const r = a.result;
          return (
            <div key={a.sessionId} style={{ padding: 8, borderBottom: '1px solid #333' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: cat.color }}>{f.filterCategory === 'dropped' ? '⚠' : '✗'}</span>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 'bold' }}>
                  #{a.index + 1} {a.stunServers.map(s => s.replace('stun:', '')).join(' + ')}
                </span>
                <Badge color={cat.color}>{cat.label}</Badge>
              </div>
              <div style={{ fontSize: 10, color: '#bbb', marginLeft: 16, lineHeight: 1.5 }}>
                <div>原因: <span style={{ color: cat.color }}>{f.filterReason}</span></div>
                {f.filterCategory !== 'dropped' && a.failedStep !== undefined && (
                  <div>
                    阶段: <span style={{ color: '#888' }}>步骤 {a.failedStep}</span>
                    {a.failedSide && <span style={{ color: '#888' }}> · [{a.failedSide.toUpperCase()}]</span>}
                  </div>
                )}
                {(f.filterCategory === 'quality' || f.filterCategory === 'dropped') && r && (
                  <div>
                    {r.throughputKbps !== undefined && <>吞吐 <span style={{ color: '#4caf50' }}>{r.throughputKbps} kbps</span></>}
                    {r.rttMs !== undefined && <> · RTT <span style={{ color: '#81c784' }}>{r.rttMs.toFixed(0)}ms</span></>}
                    {r.connectionType && <> · 类型 <span style={{ color: '#64b5f6' }}>{r.connectionType}</span></>}
                  </div>
                )}
                <div style={{ color: '#666' }}>
                  {f.filterCategory === 'dropped' ? '掉线于 ' : '失败于 '}
                  {formatTime(f.filteredAt)}
                  {a.round !== undefined && <span style={{ marginLeft: 6 }}>· R{a.round}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // 当前尝试详情：4 阶段卡片 + 可选调试模式原 20 步 Timeline
  // ---------- 阶段状态胶囊 ----------
  const phaseBadge = (agg: PhaseAgg) => {
    const fmtMs = (ms?: number) =>
      ms === undefined ? '-' : (ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`);
    switch (agg.status) {
      case 'success': return <Badge color="#4caf50">✓ {fmtMs(agg.durationMs)}</Badge>;
      case 'running': return <Badge color="#2196f3">◌ {fmtMs(agg.durationMs)}</Badge>;
      case 'failed':  return <Badge color="#f44336">✗ {fmtMs(agg.durationMs)}</Badge>;
      case 'skipped': return <Badge color="#ff9800">跳过</Badge>;
      default:        return <Badge color="#888">待定</Badge>;
    }
  };

  // ---------- 单张阶段卡片（标题行 + extra 区） ----------
  const renderPhaseCard = (phase: PhaseDef, agg: PhaseAgg, extra: React.ReactNode) => (
    <div style={{ padding: '8px 10px', borderBottom: '1px solid #2a2a2a' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 'bold', color: phase.color }}>{phase.title}</span>
        <span style={{ fontSize: 10, color: '#666' }}>{agg.successCount}/{agg.totalCount}</span>
        <div style={{ flex: 1 }} />
        {phaseBadge(agg)}
      </div>
      <div style={{ fontSize: 10, color: '#bbb', lineHeight: 1.5, marginLeft: 2 }}>{extra}</div>
    </div>
  );

  // ---------- 原 20 步三列 Timeline（调试模式下展示） ----------
  const renderRawSteps = () => {
    if (!currentAttempt) return null;
    const webSteps = currentAttempt.steps.filter(s => StepSides[s.step] === 'web');
    const goSteps = currentAttempt.steps.filter(s => StepSides[s.step] === 'go');
    const bothSteps = currentAttempt.steps.filter(s => StepSides[s.step] === 'both');
    const renderStep = (s: StepStatus) => (
      <div key={s.step} style={{ display: 'flex', alignItems: 'flex-start', padding: '2px 0', opacity: (s.status === 'pending' || s.status === 'skipped') ? 0.5 : 1 }}>
        <span style={{ marginRight: 6, fontSize: 11, color: getStepColor(s.status), fontWeight: 'bold' }}>{getStepIcon(s.status)}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 11 }}>{s.step}. {s.name}</span>
          {s.details && <div style={{ fontSize: 10, color: '#888', marginLeft: 12 }}>{s.details}</div>}
          {s.error && <div style={{ fontSize: 10, color: '#f44336', marginLeft: 12 }}>{s.error}</div>}
        </div>
      </div>
    );
    return (
      <div style={{ padding: '8px 10px', background: '#1e1e1e', borderTop: '1px dashed #333' }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 11, color: '#64b5f6' }}>WEB 端</div>
            {webSteps.map(renderStep)}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 11, color: '#81c784' }}>HOST 端</div>
            {goSteps.map(renderStep)}
          </div>
        </div>
        <div style={{ marginTop: 6, borderTop: '1px solid #333', paddingTop: 6 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 11, color: '#ffb74d' }}>双端协同</div>
          {bothSteps.map(renderStep)}
        </div>
      </div>
    );
  };

  const renderCurrentAttempt = () => {
    if (!currentAttempt || currentAttempt.status === 'pending') {
      return <div style={{ padding: 12, textAlign: 'center', color: '#888', fontSize: 12 }}>等待开始...</div>;
    }

    // ---------- 阶段聚合 ----------
    const signal  = aggregatePhase(currentAttempt.steps, PHASES[0].stepIds, nowMs);
    const sync    = aggregatePhase(currentAttempt.steps, PHASES[1].stepIds, nowMs);
    const ice     = aggregatePhase(currentAttempt.steps, PHASES[2].stepIds, nowMs);
    const quality = aggregatePhase(currentAttempt.steps, PHASES[3].stepIds, nowMs);

    // ---------- 标题栏 ----------
    const attemptStart = currentAttempt.startTime;
    const attemptEnd = currentAttempt.endTime ?? nowMs;
    const totalElapsed = Math.max(0, attemptEnd - attemptStart);
    const totalElapsedText = totalElapsed < 1000 ? `${Math.round(totalElapsed)}ms` : `${(totalElapsed / 1000).toFixed(1)}s`;
    // 同 STUN 组合的历史失败次数（不含自己）
    const stunKey = currentAttempt.stunServers.join(',');
    const sameGroupFailed = state.attempts.filter(a =>
      a !== currentAttempt &&
      a.stunServers.join(',') === stunKey &&
      a.status === 'failed'
    ).length;
    const statusBadge = (() => {
      switch (currentAttempt.status) {
        case 'success': return <Badge color="#4caf50">✓ 完成 {totalElapsedText}</Badge>;
        case 'running': return <Badge color="#2196f3">● 进行中 {totalElapsedText}</Badge>;
        case 'failed':  return <Badge color="#f44336">✗ 失败 {totalElapsedText}</Badge>;
        default:        return <Badge color="#888">待开始</Badge>;
      }
    })();

    // ---------- 各阶段 extra 内容 ----------
    const signalExtra = (() => {
      if (!signal.hasData) return <span style={{ color: '#666' }}>未开始</span>;
      if (signal.status === 'failed' && signal.failedStep) {
        const side = StepSides[signal.failedStep.step];
        return (
          <span>
            Offer → Answer → SetRemote
            <span style={{ color: '#f44336', marginLeft: 8 }}>
              · [{side?.toUpperCase()}] 步骤{signal.failedStep.step} {signal.failedStep.name}
            </span>
          </span>
        );
      }
      return <span>Offer → Answer → SetRemote <span style={{ color: '#666', marginLeft: 4 }}>(web → host → web)</span></span>;
    })();

    const syncExtra = (() => {
      if (!sync.hasData) return <span style={{ color: '#666' }}>未开始</span>;
      const hasAny =
        syncInfo && (
          syncInfo.rttMedianMs !== undefined ||
          syncInfo.clockOffsetMs !== undefined ||
          syncInfo.syncAccuracyMs !== undefined ||
          syncInfo.holePunchMethod !== undefined
        );
      if (!hasAny) return <span style={{ color: '#666' }}>进行中...</span>;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {syncInfo?.rttMedianMs !== undefined && (
            <span>RTT 中位 <b style={{ color: '#81c784' }}>{syncInfo.rttMedianMs}ms</b></span>
          )}
          {syncInfo?.clockOffsetMs !== undefined && (
            <span>时钟偏差 <b style={{ color: '#e0e0e0' }}>{syncInfo.clockOffsetMs > 0 ? '+' : ''}{syncInfo.clockOffsetMs}ms</b></span>
          )}
          {syncInfo?.syncAccuracyMs !== undefined && (
            <span>点火精度 <b style={{ color: '#ffc107' }}>±{syncInfo.syncAccuracyMs}ms</b></span>
          )}
          {holePunchBadge(syncInfo?.holePunchMethod)}
        </div>
      );
    })();

    const iceExtra = (() => {
      if (!ice.hasData) return <span style={{ color: '#666' }}>等待 Sync 完成</span>;
      const r = currentAttempt.result;
      if (r?.localCandidate || r?.remoteCandidate) {
        return (
          <div style={{ fontFamily: 'monospace', fontSize: 10 }}>
            <div><span style={{ color: '#64b5f6' }}>本地:</span> {r.localCandidate || '-'}</div>
            <div><span style={{ color: '#81c784' }}>远端:</span> {r.remoteCandidate || '-'}</div>
          </div>
        );
      }
      if (ice.status === 'running') return <span style={{ color: '#666' }}>候选对协商中...</span>;
      return <span style={{ color: '#666' }}>候选对未披露</span>;
    })();

    const qualityExtra = (() => {
      if (!quality.hasData) return <span style={{ color: '#666' }}>等待 ICE 建立</span>;
      const r = currentAttempt.result;
      if (!r) return <span style={{ color: '#666' }}>采集中...</span>;
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <span>吞吐 <b style={{ color: '#4caf50' }}>{r.throughputKbps ?? '-'} kbps</b></span>
          {r.rttMs !== undefined && (
            <span>RTT <b style={{ color: '#81c784' }}>{r.rttMs.toFixed(0)}ms</b></span>
          )}
          <span>类型 <b style={{ color: '#64b5f6' }}>{r.connectionType}</b></span>
          {r.totalTimeMs !== undefined && (
            <span style={{ color: '#888' }}>总耗时 {r.totalTimeMs}ms</span>
          )}
        </div>
      );
    })();

    // ---------- 失败定位 ----------
    const failedPhase = currentAttempt.failedStep !== undefined
      ? PHASES.find(p => p.stepIds.includes(currentAttempt.failedStep as number))
      : undefined;

    return (
      <div>
        {/* 标题栏 */}
        <div style={{ padding: '8px 10px', borderBottom: '1px solid #333', background: '#242424' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 'bold', color: '#e0e0e0' }}>
              当前尝试 #{currentAttempt.index + 1}
            </span>
            {statusBadge}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 10, color: '#aaa' }}>第 {state.roundCount}/{state.maxRounds} 轮</span>
          </div>
          <div style={{ fontSize: 10, color: '#888' }}>
            STUN: {currentAttempt.stunServers.map(s => s.replace('stun:', '')).join(' + ')}
            {sameGroupFailed > 0 && (
              <span style={{ color: '#ff9800', marginLeft: 8 }}>· 此组合已失败 {sameGroupFailed} 次</span>
            )}
          </div>
          {/* 候选对详情（双视角实时刷新：web 端 500ms 轮询自己的 selectedPair；
              host 通过主连接 ctrl DC 每 500ms 上报 host_stats） */}
          {(() => {
            const sp = currentAttempt.diagnostics?.finalStats?.selectedPair;
            const hp = currentAttempt.diagnostics?.hostFinalStats;
            const stateColor = (s: string | undefined) => s === 'succeeded' ? '#4caf50' : '#ff9800';
            return (
              <div style={{ marginTop: 6, padding: 6, background: '#1a1a1a', border: '1px solid #333', borderRadius: 3, fontSize: 10, fontFamily: 'monospace', lineHeight: 1.6 }}>
                <div style={{ color: '#90caf9', fontSize: 10, fontWeight: 'bold', marginBottom: 4, fontFamily: 'inherit' }}>🌐 候选对详情</div>

                {/* Web 视角 */}
                <div style={{ color: '#9ccc65', fontSize: 9, marginBottom: 1 }}>─ Web 视角 ─</div>
                {sp ? (
                  <>
                    <div>
                      <span style={{ color: '#64b5f6' }}>本地公网:</span>{' '}
                      <b style={{ color: '#e0e0e0' }}>{sp.localAddress}:{sp.localPort}</b>
                      <span style={{ color: '#888' }}> [{sp.localType}]</span>
                    </div>
                    <div>
                      <span style={{ color: '#81c784' }}>远端公网:</span>{' '}
                      <b style={{ color: '#e0e0e0' }}>{sp.remoteAddress}:{sp.remotePort}</b>
                      <span style={{ color: '#888' }}> [{sp.remoteType}]</span>
                    </div>
                    <div style={{ color: '#aaa' }}>
                      状态: <b style={{ color: stateColor(sp.state) }}>{sp.state}</b>
                      {' · '}
                      STUN包: 发 <b style={{ color: '#e0e0e0' }}>{sp.requestsSent ?? 0}</b> / 收 <b style={{ color: '#e0e0e0' }}>{sp.responsesReceived ?? 0}</b>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#666' }}>web 协商中...</div>
                )}

                {/* Host 视角 */}
                <div style={{ color: '#9ccc65', fontSize: 9, marginTop: 4, marginBottom: 1 }}>─ Host 视角 ─</div>
                {hp ? (
                  <>
                    <div>
                      <span style={{ color: '#64b5f6' }}>本地公网:</span>{' '}
                      <b style={{ color: '#e0e0e0' }}>{hp.localAddress}:{hp.localPort}</b>
                      <span style={{ color: '#888' }}> [{hp.localType}]</span>
                    </div>
                    <div>
                      <span style={{ color: '#81c784' }}>远端公网:</span>{' '}
                      <b style={{ color: '#e0e0e0' }}>{hp.remoteAddress}:{hp.remotePort}</b>
                      <span style={{ color: '#888' }}> [{hp.remoteType}]</span>
                    </div>
                    <div style={{ color: '#aaa' }}>
                      状态: <b style={{ color: stateColor(hp.state) }}>{hp.state}</b>
                      {' · '}
                      STUN包: 发 <b style={{ color: '#e0e0e0' }}>{hp.requestsSent ?? 0}</b> / 收 <b style={{ color: '#e0e0e0' }}>{hp.responsesReceived ?? 0}</b>
                    </div>
                  </>
                ) : (
                  <div style={{ color: '#666' }}>等待host消息</div>
                )}
              </div>
            );
          })()}
        </div>

        {/* 4 阶段卡片 */}
        {renderPhaseCard(PHASES[0], signal,  signalExtra)}
        {renderPhaseCard(PHASES[1], sync,    syncExtra)}
        {renderPhaseCard(PHASES[2], ice,     iceExtra)}
        {renderPhaseCard(PHASES[3], quality, qualityExtra)}

        {/* 失败摘要（阶段 + step 映射） */}
        {currentAttempt.status === 'failed' && currentAttempt.failedReason && (
          <div style={{ margin: '6px 10px 4px', padding: 8, background: 'rgba(244,67,54,0.1)', border: '1px solid #f44336', borderRadius: 4, fontSize: 11 }}>
            <div style={{ color: '#f44336', fontWeight: 'bold', marginBottom: 2 }}>
              ✗ {failedPhase?.title ?? '未知阶段'} · [{currentAttempt.failedSide?.toUpperCase()}] 步骤{currentAttempt.failedStep}
            </div>
            <div style={{ color: '#f88' }}>{currentAttempt.failedReason}</div>
          </div>
        )}

        {/* 调试模式开关 + 原 20 步 Timeline */}
        <div style={{
          padding: '6px 10px', borderTop: '1px solid #333',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <input
            type="checkbox"
            id="p2pAssistDbgMode"
            checked={debugMode}
            onChange={toggleDebugMode}
            style={{ cursor: 'pointer' }}
          />
          <label htmlFor="p2pAssistDbgMode" style={{ fontSize: 10, color: '#888', cursor: 'pointer', userSelect: 'none' }}>
            显示所有 20 步（调试模式）
          </label>
        </div>
        {debugMode && renderRawSteps()}
      </div>
    );
  };

  // 复制全部日志为纯文本（顶部「打洞日志」按钮使用）
  const copyLogs = async () => {
    const text = state.logs.map(log => {
      const side = `[${log.side.toUpperCase()}]`;
      const step = log.step ? ` S${log.step}:` : '';
      return `${formatTime(log.timestamp)} ${side}${step} ${log.message}`;
    }).join('\n');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // HTTPS 以外或旧浏览器 fallback：textarea + execCommand
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      eventBus.emit('ui:toast', { message: `已复制 ${state.logs.length} 条日志`, type: 'success' });
    } catch (e) {
      eventBus.emit('ui:toast', { message: `复制失败: ${(e as Error).message}`, type: 'error' });
    }
  };

  // ---------- 主体 ----------
  return (
    <div
      ref={panelRef}
      data-ui-element="true"
      onMouseDown={(e) => e.nativeEvent.stopImmediatePropagation()}
      onMouseUp={(e) => e.nativeEvent.stopImmediatePropagation()}
      onMouseMove={(e) => e.nativeEvent.stopImmediatePropagation()}
      onClick={(e) => e.nativeEvent.stopImmediatePropagation()}
      onContextMenu={(e) => e.nativeEvent.stopImmediatePropagation()}
      style={{
        position: 'fixed', left: position.x, top: position.y,
        width: 560, maxHeight: 'calc(100vh - 80px)',
        background: '#1e1e1e', border: '1px solid #444', borderRadius: 6,
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', flexDirection: 'column', color: '#e0e0e0', fontSize: 12,
      }}
    >
      {/* 标题栏 */}
      <div onMouseDown={handleMouseDown} style={{
        padding: '6px 10px', background: '#2d2d2d', borderBottom: '1px solid #444',
        display: 'flex', alignItems: 'center', borderRadius: '6px 6px 0 0',
        cursor: 'grab', userSelect: 'none',
      }}>
        <span style={{ flex: 1, fontWeight: 'bold', fontSize: 12 }}>P2P Assist · 实时监控</span>
        <button
          onClick={copyLogs}
          title="复制全部打洞日志（每次启动会清空旧日志）"
          style={{
            padding: '2px 8px', marginRight: 4,
            background: '#37474f',
            border: 'none', borderRadius: 3, color: 'white', cursor: 'pointer', fontSize: 10,
          }}
        >📋 打洞日志</button>
        <button
          onClick={async () => {
            try {
              const report = p2pAssistService.buildReport();
              const text = JSON.stringify(report, null, 2);
              if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
              } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
              }
              const attempts = (report['尝试列表'] as unknown[] | undefined)?.length ?? 0;
              const sizeKB = Math.ceil(text.length / 1024);
              eventBus.emit('ui:toast', {
                message: `已复制打洞报告（${attempts} 个尝试 / ${sizeKB} KB）`,
                type: 'success',
              });
            } catch (e) {
              eventBus.emit('ui:toast', { message: `复制失败: ${(e as Error).message}`, type: 'error' });
            }
          }}
          title="复制完整打洞报告 JSON 到剪贴板（始终可用，跨轮累积）"
          style={{
            padding: '2px 8px', marginRight: 4,
            background: '#455a64',
            border: 'none', borderRadius: 3, color: 'white', cursor: 'pointer', fontSize: 10,
          }}
        >📋 打洞报告</button>
        <button
          onClick={() => state.isRunning ? p2pAssistService.stop() : p2pAssistService.start()}
          style={{
            padding: '2px 8px', marginRight: 4,
            background: state.isRunning ? '#c62828' : '#388e3c',
            border: 'none', borderRadius: 3, color: 'white', cursor: 'pointer', fontSize: 10,
          }}
        >{state.isRunning ? '停止' : '开始'}</button>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}>×</button>
      </div>

      {/* 4 张指标卡 */}
      <div style={{ display: 'flex', gap: 6, padding: 8, background: '#1a1a1a' }}>
        <Card
          title="主连接"
          value={state.mainConnectionType === 'relay' ? 'RELAY' : state.mainConnectionType === 'p2p' ? 'P2P' : '-'}
          sub={state.isRunning ? '→升级中' : state.mainConnectionType === 'p2p' ? '已直连' : '就绪'}
          color={state.mainConnectionType === 'relay' ? '#ff9800' : state.mainConnectionType === 'p2p' ? '#4caf50' : '#888'}
        />
        <Card
          title="RTT 中位"
          value={syncInfo?.rttMedianMs !== undefined ? `${syncInfo.rttMedianMs.toFixed(0)}ms` : '--'}
          sub={syncInfo?.rttSamples?.length ? `${syncInfo.rttSamples.length} 样本` : '未测'}
          color="#81c784"
        />
        <Card
          title="本轮进度"
          value={(() => {
            const total = state.attempts.length || 6;
            // 运行中：显示当前 attempt 进度（即使有 established 也继续展示）
            if (state.isRunning) {
              if (state.currentAttemptIndex >= total) return `${total}/${total}`;
              return `${state.currentAttemptIndex + 1}/${total}`;
            }
            // 非运行态有 bestAttempt（切换后 / 手动停止后）：显示获胜编号
            if (state.bestAttempt) return `${state.bestAttempt.index + 1}/${total}`;
            // 其他（未启动）
            return `·/${total}`;
          })()}
          sub={`R${state.roundCount}/${state.maxRounds}`}
          color="#64b5f6"
        />
        <Card
          title="打洞状态"
          value={
            holePunchStatus === 'established' ? '✅ 已打通' :
            holePunchStatus === 'running' ? '⏳ 打洞中' : '· 未开始'
          }
          sub={(() => {
            // N4: 60s 倒计时已移到独立块,这里只显示 established 数量
            if (state.establishedConnections.length > 0) return `${state.establishedConnections.length} 条 P2P`;
            if (state.isRunning) return '探测中…';
            return '--';
          })()}
          color={
            holePunchStatus === 'established' ? '#4caf50' :
            holePunchStatus === 'running' ? '#64b5f6' : '#888'
          }
        />
      </div>

      {/* N4: 60s 强制切换倒计时独立块(与 Flutter 对齐) */}
      {state.firstEstablishedAt && state.establishedConnections.length > 0 && (() => {
        const elapsed = Math.min(state.forceSwitchTimeoutMs, Math.max(0, nowMs - state.firstEstablishedAt));
        const remainSec = Math.max(0, Math.ceil((state.forceSwitchTimeoutMs - elapsed) / 1000));
        const progress = elapsed / state.forceSwitchTimeoutMs;
        return (
          <div style={{ padding: '6px 8px', background: '#2a2a2a', borderBottom: '1px solid #333' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ color: '#ff9800', fontSize: 10, fontWeight: 'bold' }}>⏱ 强制切换倒计时</span>
              <span style={{ flex: 1 }} />
              <span style={{ color: '#e0e0e0', fontSize: 10 }}>剩余 {remainSec}s</span>
            </div>
            <div style={{ height: 4, background: '#333', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${progress * 100}%`, height: '100%',
                background: '#ff9800', transition: 'width 0.5s',
              }} />
            </div>
          </div>
        );
      })()}

      {/* 同步信息区（NAT 分型已移除，只展示 Sync 打洞诊断）*/}
      <div style={{ padding: 8, background: '#242424', borderTop: '1px solid #333', borderBottom: '1px solid #333' }}>
        <div style={{ fontSize: 11, fontWeight: 'bold', color: '#ab47bc', marginBottom: 6 }}>⚡ 同步信息</div>
        <div style={{ fontSize: 10, color: '#888', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {/* N1: 阶段字段(与 Flutter 对齐) */}
          {syncInfo?.phase && (
            <span>阶段: <span style={{ color: '#ab47bc' }}>{syncInfo.phase}</span></span>
          )}
          {syncInfo?.clockOffsetMs !== undefined && (
            <span>时钟偏差: <span style={{ color: '#ffb74d' }}>{syncInfo.clockOffsetMs > 0 ? '+' : ''}{syncInfo.clockOffsetMs.toFixed(0)}ms</span></span>
          )}
          {syncInfo?.fireAtMs !== undefined && (
            <span>fire_at: <span style={{ color: '#ffc107' }}>{formatTime(Date.now() + (syncInfo.fireAtMs - performance.now()))}</span></span>
          )}
          {syncInfo?.syncAccuracyMs !== undefined && (
            <span>点火误差: <span style={{ color: syncInfo.syncAccuracyMs < 50 ? '#4caf50' : '#ff9800' }}>±{syncInfo.syncAccuracyMs}ms</span></span>
          )}
          {syncInfo?.holePunchMethod && <span>方式: {holePunchBadge(syncInfo.holePunchMethod)}</span>}
          {!syncInfo?.phase && !syncInfo?.clockOffsetMs && !syncInfo?.fireAtMs && !syncInfo?.syncAccuracyMs && !syncInfo?.holePunchMethod && (
            <span style={{ color: '#666' }}>无同步数据</span>
          )}
        </div>
      </div>

      {/* 当前阶段进度条 */}
      <div style={{ padding: 8, background: '#1e1e1e', borderBottom: '1px solid #333' }}>
        <div style={{ fontSize: 11, fontWeight: 'bold', color: '#2196f3', marginBottom: 6 }}>⚡ 当前阶段</div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
          {phases.map((p, i) => {
            const active = i === curPhaseIdx;
            const passed = i < curPhaseIdx;
            return (
              <div key={p.key} style={{
                flex: 1, textAlign: 'center', padding: '4px 2px',
                borderRadius: 3, fontSize: 10,
                background: active ? '#1565c0' : passed ? 'rgba(76,175,80,0.15)' : '#2a2a2a',
                color: active ? 'white' : passed ? '#4caf50' : '#666',
                border: active ? '1px solid #64b5f6' : '1px solid transparent',
              }}>
                <span style={{ marginRight: 2 }}>{passed ? '✓' : p.icon}</span>{p.label}
              </div>
            );
          })}
        </div>
        <div style={{ height: 4, background: '#2a2a2a', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${phaseProgress}%`, height: '100%', background: 'linear-gradient(90deg,#1565c0,#64b5f6)', transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* 最佳通道区 */}
      <div style={{ background: 'rgba(76,175,80,0.05)', borderBottom: '1px solid #333' }}>
        <div style={{ padding: '6px 10px', fontSize: 11, fontWeight: 'bold', color: '#ffc107', borderBottom: state.bestAttempt ? '1px solid #333' : 'none' }}>
          ⭐ 最佳通道
        </div>
        {renderBestChannel()}
      </div>

      {/* 折叠区 */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ borderBottom: '1px solid #333' }}>
          <SectionHeader title="已建立 P2P 连接" sec="established" count={state.establishedConnections.length} color="#4caf50" />
          {sections.established && renderEstablished()}
        </div>
        <div style={{ borderBottom: '1px solid #333' }}>
          <SectionHeader title="被过滤的 P2P 连接" sec="filtered" count={state.filteredAttempts?.length ?? 0} color="#ff9800" />
          {sections.filtered && renderFiltered()}
        </div>
        {/* 当前尝试详情：强制常开，不提供折叠 */}
        <div style={{ borderBottom: '1px solid #333' }}>
          <div style={{
            padding: '6px 8px', background: '#2a2a2a', fontWeight: 'bold', fontSize: 12,
            display: 'flex', alignItems: 'center', userSelect: 'none',
            borderBottom: '1px solid #333',
          }}>
            <span style={{ marginRight: 6, fontSize: 10, color: '#666' }}>▼</span>
            <span style={{ color: '#e0e0e0' }}>当前尝试详情</span>
          </div>
          {renderCurrentAttempt()}
        </div>
      </div>
    </div>
  );
};

export default P2PAssistPanel;
